/**
 * Compliance Mapper
 * Maps DTOs to Domain Entities
 */

import {
  ComplianceTypeEntity,
  EnabledComplianceTypeEntity,
  MasterCalendarEntity,
  GroupedCalendarEntries,
  SyncStatusEntity,
  TenantComplianceEntity,
  ComplianceProofEntity,
} from "../../domain/entities";
import {
  ComplianceTypeDTO,
  EnabledComplianceTypeDTO,
  MasterCalendarDTO,
  GroupedCalendarResponseDTO,
  TenantComplianceDTO,
  ComplianceProofDTO,
} from "../models/ComplianceDTOs";

export class ComplianceMapper {
  static toComplianceTypeEntity(dto: ComplianceTypeDTO): ComplianceTypeEntity {
    return {
      id: dto.id,
      compliance_type: dto.compliance_type,
      compliance_name: dto.compliance_name,
      authority: dto.authority,
      frequency: dto.frequency,
      description: dto.description,
      is_active: dto.is_active,
      display_order: dto.display_order || 0,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      created_by: dto.created_by,
      updated_by: dto.updated_by,
      // Tenant-specific fields (may be present when fetched for tenant admin)
      is_enabled: dto.is_enabled ?? false,
      enabled_id: dto.enabled_id ?? undefined,
    };
  }

  static toComplianceTypeEntityArray(
    dtos: ComplianceTypeDTO[]
  ): ComplianceTypeEntity[] {
    return dtos.map((dto) => this.toComplianceTypeEntity(dto));
  }

  static toEnabledComplianceTypeEntity(
    dto: EnabledComplianceTypeDTO
  ): EnabledComplianceTypeEntity {
    return {
      id: dto.id,
      compliance_type_id: dto.compliance_type_id,
      is_enabled: dto.is_enabled,
      enabled_from_date: dto.enabled_from_date,
      enabled_until_date: dto.enabled_until_date,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      master_type: dto.master_type
        ? this.toComplianceTypeEntity(dto.master_type)
        : undefined,
    };
  }

  static toEnabledComplianceTypeEntityArray(
    dtos: EnabledComplianceTypeDTO[]
  ): EnabledComplianceTypeEntity[] {
    return dtos.map((dto) => this.toEnabledComplianceTypeEntity(dto));
  }

  static toMasterCalendarEntity(
    dto: MasterCalendarDTO & { status?: string }
  ): MasterCalendarEntity {
    return {
      id: dto.id,
      compliance_type_id: dto.compliance_type_id,
      compliance_form_name: dto.compliance_form_name,
      period: dto.period,
      period_start_date: dto.period_start_date,
      period_end_date: dto.period_end_date,
      due_date: dto.due_date,
      authority: dto.authority,
      is_active: dto.is_active,
      status: dto.status, // Preserve status field
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      created_by: dto.created_by,
      updated_by: dto.updated_by,
    };
  }

  static toMasterCalendarEntityArray(
    dtos: Array<MasterCalendarDTO & { status?: string }>
  ): MasterCalendarEntity[] {
    return dtos.map((dto) => this.toMasterCalendarEntity(dto));
  }

  static toGroupedCalendarEntries(
    response: GroupedCalendarResponseDTO
  ): GroupedCalendarEntries {
    const grouped: GroupedCalendarEntries = {};

    Object.keys(response.data).forEach((typeId) => {
      const group = response.data[typeId];
      grouped[typeId] = {
        type: this.toComplianceTypeEntity(group.type),
        entries: this.toMasterCalendarEntityArray(group.entries),
      };
    });

    return grouped;
  }

  static toSyncStatusEntity(data: {
    last_synced_at?: string;
    synced_by?: number;
    records_created: number;
    records_updated: number;
    errors?: string[] | null;
    created_at?: string;
  }): SyncStatusEntity {
    return {
      last_synced_at: data.last_synced_at,
      synced_by: data.synced_by,
      records_created: data.records_created,
      records_updated: data.records_updated,
      errors: data.errors || undefined,
      created_at: data.created_at,
    };
  }

  static toTenantComplianceEntity(
    dto: TenantComplianceDTO
  ): TenantComplianceEntity {
    return {
      id: dto.id,
      tenant_id: dto.tenant_id,
      compliance_type_id: dto.compliance_type_id,
      master_calendar_id: dto.master_calendar_id,
      compliance_form_name: dto.compliance_form_name,
      period: dto.period,
      period_start_date: dto.period_start_date,
      period_end_date: dto.period_end_date,
      due_date: dto.due_date,
      status: dto.status as TenantComplianceEntity["status"],
      accountant_id: dto.accountant_id,
      manager_id: dto.manager_id,
      manager_confirmed_at: dto.manager_confirmed_at,
      submitted_for_review_at: dto.submitted_for_review_at,
      review_request_id: dto.review_request_id,
      notes: dto.notes,
      synced_at: dto.synced_at,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      proof_count: dto.proof_count,
      has_primary_proof: dto.has_primary_proof,
      compliance_type: dto.compliance_type,
    };
  }

  static toTenantComplianceEntityArray(
    dtos: TenantComplianceDTO[]
  ): TenantComplianceEntity[] {
    return dtos.map((dto) => this.toTenantComplianceEntity(dto));
  }

  static toComplianceProofEntity(
    dto: ComplianceProofDTO
  ): ComplianceProofEntity {
    return {
      id: dto.id,
      compliance_id: dto.compliance_id,
      proof_type: dto.proof_type,
      file_url: dto.file_url,
      file_name: dto.file_name,
      file_size: dto.file_size,
      mime_type: dto.mime_type,
      uploaded_by: dto.uploaded_by,
      uploaded_at: dto.uploaded_at,
      description: dto.description,
      is_primary: dto.is_primary,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
      signed_url: dto.signed_url,
    };
  }

  static toComplianceProofEntityArray(
    dtos: ComplianceProofDTO[]
  ): ComplianceProofEntity[] {
    return dtos.map((dto) => this.toComplianceProofEntity(dto));
  }
}

